import { useSelector } from 'react-redux';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { useDocumentsNavigation } from '@v2/pages/common/hooks/useDocumentsNavigation';
import { NowSubScreen } from '@v2/pages/machines/common/enums';
import { ProductionOrderDetailsScreenName } from '@v2/pages/productionOrder/navigation/config';
import { ProductionOrderDetailsScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { PartDetailsScreenName } from '@v2/pages/part/details/navigation/config';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { JobDetailsScreenName } from '@v2/pages/job/navigation/config';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { selectJobsData } from '@reducers/details';
import { ProductionOrderContentProps } from '@v2/pages/productionOrder/modules/productionOrder/components/ProductionOrderContent';

export interface UseManageProductionOrderContentParams extends Pick<ProductionOrderContentProps, 'productionOrder'> {}

export const useManageProductionOrderContent = ({ productionOrder }: UseManageProductionOrderContentParams) => {
  const jobs = useSelector(selectJobsData);

  const { safeIsNowSubScreenCheck } = useNavigationCheck();
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const { handleNowScreenNavigation } = useDocumentsNavigation();

  const onProductionOrderImageClick = (selectedImageKey: string) => {
    if (safeIsNowSubScreenCheck) {
      handleNowScreenNavigation({ nowSubScreen: NowSubScreen.NC_FILE, params: { selectedImageKey } });
      return;
    }

    navigateToMainDetailsScreen({
      mainScreen: ProductionOrderDetailsScreenName,
      subScreen: ProductionOrderDetailsScreen.ORDER,
      levelSubScreen: LevelSubScreen.DOCUMENTS,
      params: {
        perspective: Perspective.ORDER,
        productionOrderId: productionOrder?.productionOrderId,
        selectedImageKey,
      },
    });
  };

  const onPartNumberClick = () => {
    navigateToMainDetailsScreen({
      mainScreen: PartDetailsScreenName,
      subScreen: PartDetailsScreen.PART,
      params: {
        perspective: Perspective.PART,
        partId: productionOrder?.partId,
        partName: productionOrder?.partNumber,
      },
    });
  };

  const onJobLineClick = (productionOperationId: number) => {
    navigateToMainDetailsScreen({
      mainScreen: JobDetailsScreenName,
      subScreen: JobDetailsScreen.JOBS,
      params: {
        perspective: Perspective.JOBS,
        productionOperationId,
      },
    });
  };

  return {
    jobs,
    onProductionOrderImageClick,
    onPartNumberClick,
    onJobLineClick,
  };
};
